import { PortalTelemetryEvent } from '../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../app/telemetry/validationTelemetry';
import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper } from "@mui/material";
import AppTextInput from "../../app/components/AppTextInput";
import { FormProvider, useForm } from "react-hook-form";
import AppAutocomplete from "../../app/components/AppAutocomplete";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { EventDetailsValidationSchema } from "./common/eventApprovalValidationSchemas";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { useReferenceData } from "../../app/customHooks/useReferenceData";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_NEXT, MESSAGE_NOT_A_VALUE, MESSAGE_PROCESSING_DETAILS, MESSAGE_SAVE_AND_CONTINUE } from "../../app/utils/constant";
import { MapCountriesToAutocompleteItems, MapCountrySubdivisionToAutocompleteItems, MapDisciplinesToAutocompleteItems, MapParticipantsRangeToAutocompleteItems, MapPcsActivityTypesToAutocompleteItems } from "../../app/utils/convertStaticDataToDropdownItems";
import { PcsEventDto } from "../../app/models/events/pcsEventDto";
import { useEffect, useState } from "react";
import { UserClaims } from "../../app/enums/userClaims";
import { useMsal } from "@azure/msal-react";
import { gePcsEventByIdAsync, savePcsEventAsync, updatePcsEventAsync } from "../../app/slices/pcsEventSlice";
import { toast } from "react-toastify";
import AppDatePicker from "../../app/components/AppDatePicker";
import { formatDate, getLocalDate } from "../../app/utils/util";
import { PcsActivityTypeEnum } from "../../app/enums/pcsActivityTypeEnum";
import { SchemEnum } from "../../app/enums/schemEnum";

const { AcademicDiscipline, TitleOfActivity, TypeOfActivity, OtherTypeOfActivity, TypeOfActivityCode, ExpectedNumberOfParticipants,
    IntendedAudience, EventStartDate, EventCountry, EventCounty, EventVenue } = EventsFormModel.eventDetailsFormField;

interface Props {
    isSpaEditabe: boolean;
    handleNext: () => void;
    handleBack: () => void;
}

export default function EventDetails({ isSpaEditabe, handleNext, handleBack }: Props) {
    const { accounts } = useMsal();
    const methods = useForm<PcsEventDto>({
        mode: 'all',
        resolver: yupResolver(EventDetailsValidationSchema as any)
    });
    const form = methods.watch();
    const { control, reset, trigger, getValues, setValue, watch, formState: { isDirty } } = methods;

    const { staticDataStateStatus } = useReferenceData();
    const { countries, countrySubdivisions, schemes, pcsActivityTypes, activityParticipantsRange }
        = useAppSelector(state => state.ReferenceData);
    const { pcsEventDetails, getPcsEventStatus, savePcsEventStatus, updatePcsEventStatus } = useAppSelector(state => state.pcsEvent);

    const [personId, setPersonId] = useState('');
    const [eventId,] = useState(new URLSearchParams(window.location.search).get("eventId"));
    const [cloneEventId,] = useState(new URLSearchParams(window.location.search).get("cloneEventId"));

    useEffect(() => {
        if (accounts.length > 0 && accounts[0]?.idTokenClaims) {
            setPersonId(accounts[0].idTokenClaims[UserClaims.EXTENSION_PORTALCRM_PERSONID] as string);
        }
    }, [accounts]);

    const MapToPcsEvent = (values: any, pcsEventDetailsObj: PcsEventDto) => {
        const pcsEventDtoObj: PcsEventDto = {
            ...pcsEventDetailsObj,
            eventId: pcsEventDetails?.eventId === undefined ? null : pcsEventDetails.eventId,
            schemeId: values.AcademicDiscipline,
            title: values.TitleOfActivity,
            activityTypeId: values.TypeOfActivity,
            otherActivityType: values.OtherTypeOfActivity,
            participantsRangeId: values.ExpectedNumberOfParticipants,
            specialty: values.IntendedAudience,
            personId: personId,
            startDate: formatDate(values.EventStartDate),
            eventCountry: values.EventCountry,
            eventCounty: values.EventCounty === MESSAGE_NOT_A_VALUE ? '' : values.EventCounty,
            eventVenue: values.EventVenue
        }
        return pcsEventDtoObj;
    }

    useEffect(() => {
        let startDate = pcsEventDetails?.startDate !== undefined && pcsEventDetails?.startDate !== null ? getLocalDate(new Date(pcsEventDetails?.startDate)) : '';
        if (cloneEventId !== null && cloneEventId !== '') startDate = '';

        if (pcsEventDetails !== null && ((eventId !== null && eventId !== '') || (cloneEventId !== null && cloneEventId !== ''))) {
            reset({
                [AcademicDiscipline.name]: pcsEventDetails?.schemeId,
                [TitleOfActivity.name]: pcsEventDetails?.title,
                [TypeOfActivity.name]: pcsEventDetails?.activityTypeId,
                [OtherTypeOfActivity.name]: pcsEventDetails?.otherActivityType,
                [ExpectedNumberOfParticipants.name]: pcsEventDetails?.participantsRangeId,
                [IntendedAudience.name]: pcsEventDetails?.specialty,
                [EventStartDate.name]: startDate,
                [EventCountry.name]: pcsEventDetails?.eventCountry,
                [EventCounty.name]: pcsEventDetails?.eventCounty,
                [EventVenue.name]: pcsEventDetails?.eventVenue
            });

            if (countrySubdivisions?.filter(x => x.countryId === pcsEventDetails?.eventCountry).length === 0) {
                setValue(EventCounty.name as keyof PcsEventDto, MESSAGE_NOT_A_VALUE);
            }
        }

        if (pcsEventDetails !== null) {
            setValue(TypeOfActivityCode.name as keyof PcsEventDto, pcsActivityTypes?.find(x => x.id === pcsEventDetails?.activityTypeId)?.code);
        }
    }, [reset, pcsEventDetails, cloneEventId, eventId, countrySubdivisions, setValue, pcsActivityTypes]);

    useEffect(() => {
        if (pcsActivityTypes && pcsActivityTypes?.length > 0) {
            const subscription = watch((value, { name, type }) => {
                if (name === TypeOfActivity.name && type === 'change') {
                    const selectedId = value[TypeOfActivity.name as keyof PcsEventDto] as string | undefined;
                    const currentPcsActivity = pcsActivityTypes?.find(x => x.id === selectedId);

                    setValue(OtherTypeOfActivity.name as keyof PcsEventDto, '');
                    setValue(TypeOfActivityCode.name as keyof PcsEventDto, currentPcsActivity?.code);

                    if (currentPcsActivity?.code === PcsActivityTypeEnum.ELearningCourse || currentPcsActivity?.code === PcsActivityTypeEnum.Webinar) {
                        setValue(EventCountry.name as keyof PcsEventDto, '');
                        setValue(EventCounty.name as keyof PcsEventDto, '');
                        setValue(EventVenue.name as keyof PcsEventDto, '');
                    }
                }

                if (name === EventCountry.name && type === 'change') {
                    if (countrySubdivisions?.filter(x => x.countryId === getValues(EventCountry.name as keyof PcsEventDto)).length === 0) {
                        setValue(EventCounty.name as keyof PcsEventDto, MESSAGE_NOT_A_VALUE);
                    }
                    else {
                        setValue(EventCounty.name as keyof PcsEventDto, '');
                    }
                }
            });
            return () => subscription.unsubscribe();
        }
    }, [countrySubdivisions, getValues, pcsActivityTypes, setValue, watch]);

    const dispatch = useAppDispatch();

    const saveDetailsAsync = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) {
            trackPortalEvent(portalScreenContext(PortalTelemetryScreen.EventDetails),
                PortalTelemetryEvent.ValidationBlocked, {
                step: 'event',
                validationErrors: validationIssues(PortalTelemetryScreen.EventDetails, methods.getFieldState)
            });
        }

        if (isStepValid && form && isDirty === true) {
            if (eventId === null && cloneEventId === null) {
                dispatch(savePcsEventAsync(MapToPcsEvent(form, {})))
                    .then((response) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            toast.success(MESSAGE_DETAILS_SAVED);
                            dispatch(gePcsEventByIdAsync(response.payload as string));
                            const url = new URL(window.location.href);
                            url.searchParams.set('eventId', response.payload as string);
                            url.searchParams.delete('cloneEventId');
                            window.history.pushState({}, '', url);
                            handleNext();
                        }
                    });
            }
            if (eventId === null && cloneEventId !== null && pcsEventDetails !== null) {
                dispatch(savePcsEventAsync(MapToPcsEvent(form, pcsEventDetails)))
                    .then((response) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            toast.success(MESSAGE_DETAILS_SAVED);
                            dispatch(gePcsEventByIdAsync(response.payload as string));
                            const url = new URL(window.location.href);
                            url.searchParams.set('eventId', response.payload as string);
                            url.searchParams.delete('cloneEventId');
                            window.history.pushState({}, '', url);
                            handleNext();
                        }
                    });
            }
            else if (pcsEventDetails?.eventId !== '' && pcsEventDetails?.eventId !== undefined) {
                dispatch(updatePcsEventAsync(MapToPcsEvent(form, pcsEventDetails)))
                    .then((response) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            toast.success(MESSAGE_DETAILS_SAVED);
                            pcsEventDetails?.eventId && dispatch(gePcsEventByIdAsync(pcsEventDetails?.eventId));
                            handleNext();
                        }
                    });
            }
        }

        if (isStepValid && isDirty === false) {
            handleNext();
        }
    }

    usePortalScreen(portalScreenContext(PortalTelemetryScreen.EventDetails), undefined, !(getPcsEventStatus === ApiRequestStatus.Pending || savePcsEventStatus === ApiRequestStatus.Pending
        || staticDataStateStatus === ApiRequestStatus.Pending
        || updatePcsEventStatus === ApiRequestStatus.Pending));

    if (getPcsEventStatus === ApiRequestStatus.Pending || savePcsEventStatus === ApiRequestStatus.Pending
        || staticDataStateStatus === ApiRequestStatus.Pending
        || updatePcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ m: "2%" }}>
                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={AcademicDiscipline.name} label={AcademicDiscipline.label}
                        items={MapDisciplinesToAutocompleteItems(schemes?.filter(x => x.code !== SchemEnum.Ophthalmology) ?? [])}
                        disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={TitleOfActivity.name} label={TitleOfActivity.label}
                        maxStringCount={TitleOfActivity.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={TypeOfActivity.name} label={TypeOfActivity.label}
                        items={MapPcsActivityTypesToAutocompleteItems(pcsActivityTypes ?? [])}
                        disabled={isSpaEditabe} />
                </Grid>

                {getValues(TypeOfActivityCode.name as keyof PcsEventDto) === PcsActivityTypeEnum.Other &&
                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={OtherTypeOfActivity.name} label={OtherTypeOfActivity.label}
                            maxStringCount={OtherTypeOfActivity.maxStringCount} disabled={isSpaEditabe} />
                    </Grid>
                }

                {getValues(TypeOfActivityCode.name as keyof PcsEventDto) !== undefined &&
                    getValues(TypeOfActivityCode.name as keyof PcsEventDto) !== "" &&
                    getValues(TypeOfActivityCode.name as keyof PcsEventDto) !== PcsActivityTypeEnum.ELearningCourse &&
                    getValues(TypeOfActivityCode.name as keyof PcsEventDto) !== PcsActivityTypeEnum.Webinar &&
                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={EventVenue.name} label={EventVenue.label}
                            maxStringCount={EventVenue.maxStringCount} disabled={isSpaEditabe} information_text={EventVenue.information_text} />
                    </Grid>
                }

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={ExpectedNumberOfParticipants.name}
                        label={ExpectedNumberOfParticipants.label} disabled={isSpaEditabe}
                        items={MapParticipantsRangeToAutocompleteItems(activityParticipantsRange ?? [])} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={IntendedAudience.name} label={IntendedAudience.label}
                        maxStringCount={IntendedAudience.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppDatePicker control={control} name={EventStartDate.name} label={EventStartDate.label}
                        defaultValue={null} disabled={isSpaEditabe} minDate={new Date()} />
                </Grid>

                {getValues(TypeOfActivityCode.name as keyof PcsEventDto) !== PcsActivityTypeEnum.ELearningCourse &&
                    getValues(TypeOfActivityCode.name as keyof PcsEventDto) !== PcsActivityTypeEnum.Webinar && <>
                        <Grid size={{ xs: 12 }}>
                            <AppAutocomplete control={control} name={EventCountry.name} label={EventCountry.label}
                                items={MapCountriesToAutocompleteItems(countries ?? [])} disabled={isSpaEditabe} />
                        </Grid>

                        {countrySubdivisions?.filter(x => x.countryId === getValues(EventCountry.name as keyof PcsEventDto)) &&
                            countrySubdivisions?.filter(x => x.countryId === getValues(EventCountry.name as keyof PcsEventDto)).length > 0 &&
                            <Grid size={{ xs: 12 }}>
                                <AppAutocomplete control={control} name={EventCounty.name} label={EventCounty.label}
                                    disabled={isSpaEditabe}
                                    items={MapCountrySubdivisionToAutocompleteItems(countrySubdivisions?.filter(
                                        x => x.countryId === getValues(EventCountry.name as keyof PcsEventDto)) ?? [])} />
                            </Grid>
                        }
                    </>}

                <Grid size={{ xs: 5 }} sx={{ textAlign: 'start' }}>
                    <Button variant="outlined" color="primary" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.EventDetails), `${PortalTelemetryScreen.EventDetails}.onClick.handleBack`, handleBack)}>
                        Back to previous step
                    </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={saveDetailsAsync}>
                        {isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 0, sm: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>
        </Box>
    </FormProvider>
}
